/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';

/**
 * Values used as keys in our local storage object.
 */
export const enum LocalStorage {
  INSTANCES = 'instances'
}

function localStorage() {
  const userStorageKey = 'conversation';
  const enabled = checkForLocalStorage();
  let cachedLocalStorageObject = null;
  let storeContext = null;

  const service = {
    isEnabled,
    get,
    set,
    getContext,
    setContext,
    remove
  };

  function checkForLocalStorage() {
    try {
      const testName = 'ibm_watson_conversation_local_storage_test';
      window.localStorage.setItem(testName, testName);
      window.localStorage.removeItem(testName);
      return true;
    } catch (e) {
      return false;
    }
  }

  function isEnabled() {
    return enabled;
  }

  function get(key: string) {
    return _.get(getCachedStorageObject(), key, null);
  }

  function set(key: string, value: any) {
    try {
      window.localStorage.setItem(
        getStorageKey(),
        JSON.stringify(_.set(getCachedStorageObject(), key, value))
      );
    } catch (error) {
      console.error(`Error calling localStorage.setItem() with ${key}`, error);
    }
  }

  function getContext() {
    return storeContext;
  }

  function setContext(context) {
    //storageKey will update with the new context, we need to reset the cached localStorage value
    cachedLocalStorageObject = null;
    storeContext = context;
  }

  function remove(key: string): boolean {
    let result: boolean = false;
    const existingStorageObj = getCachedStorageObject();

    if (_.get(existingStorageObj, key, null)) {
      result = _.unset(existingStorageObj, key);
      try {
        window.localStorage.setItem(
          getStorageKey(),
          JSON.stringify(existingStorageObj)
        );
      } catch (error) {
        console.error(
          `Error calling localStorage.setItem() with ${key}`,
          error
        );
        return false;
      }
    } else {
      result = false;
    }

    return result;
  }

  function getStorageKey(): string {
    const wContext = storeContext
      ? userStorageKey + '.' + storeContext
      : userStorageKey;
    return wContext;
  }

  function getCachedStorageObject(): Record<string, string | boolean | number> {
    if (!cachedLocalStorageObject) {
      try {
        const localStorageObject = window.localStorage.getItem(getStorageKey());
        if (localStorageObject) {
          cachedLocalStorageObject = JSON.parse(localStorageObject);
        } else {
          cachedLocalStorageObject = {};
        }
      } catch (err) {
        cachedLocalStorageObject = {};
      }
    }

    return cachedLocalStorageObject;
  }

  return service;
}

const service = localStorage();

export default service;
